// Liquify — basic push/pinch/twirl distortion brush.
// Functional approximation: applies mesh-based distortion using forward
// mapping with inverse warping for quality.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type LiquifyMode = "push" | "reconstruct" | "twirl-clockwise" | "twirl-counter" | "pinch" | "bloat" | "freeze" | "thaw";

export interface LiquifyBrush {
  x: number;
  y: number;
  radius: number;
  pressure: number; // 0..1
  density?: number; // 0..1, falloff shape
}

export interface LiquifyParams {
  mode: LiquifyMode;
  brushSize: number; // px, default 100
  brushPressure: number; // 0..100, default 50
  brushDensity: number; // 0..100, default 50
  brushRate: number; // 0..100, default 50
  /** Stroke data — array of brush positions. */
  strokes: LiquifyBrush[][];
  /** Frozen areas mask (0 = unfrozen, 255 = frozen). */
  freezeMask?: Uint8Array | null;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function applyBrushFalloff(dist: number, radius: number, density: number): number {
  if (dist >= radius) return 0;
  const t = dist / radius;
  // Density controls the falloff curve: 0 = linear, 1 = sharp center
  const d = density / 100;
  return Math.pow(1 - t, 2 * (1 - d) + 1);
}

export const Liquify: Filter<LiquifyParams> = {
  id: "liquify",
  displayName: "Liquify",
  defaultParams: {
    mode: "push",
    brushSize: 100,
    brushPressure: 50,
    brushDensity: 50,
    brushRate: 50,
    strokes: [],
    freezeMask: null,
  },

  apply(
    input: PixelBuffer,
    params: LiquifyParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0 || params.strokes.length === 0) {
      dst.set(src);
      return out;
    }

    // Build displacement map
    const dispX = new Float32Array(n);
    const dispY = new Float32Array(n);
    const freezeMask = params.freezeMask;

    for (const stroke of params.strokes) {
      for (const brush of stroke) {
        const radius = brush.radius;
        const pressure = brush.pressure * params.brushPressure / 100;
        const density = params.brushDensity;
        const rate = params.brushRate / 100;

        const x0 = Math.max(0, Math.floor(brush.x - radius));
        const x1 = Math.min(w - 1, Math.ceil(brush.x + radius));
        const y0 = Math.max(0, Math.floor(brush.y - radius));
        const y1 = Math.min(h - 1, Math.ceil(brush.y + radius));

        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            const dx = x - brush.x;
            const dy = y - brush.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist >= radius) continue;

            // Check freeze mask
            if (freezeMask && freezeMask[y * w + x]! > 128) continue;

            const falloff = applyBrushFalloff(dist, radius, density);
            const strength = pressure * rate * falloff;

            const idx = y * w + x;

            switch (params.mode) {
              case "push":
                dispX[idx] = (dispX[idx] ?? 0) + dx * strength * 0.1;
                dispY[idx] = (dispY[idx] ?? 0) + dy * strength * 0.1;
                break;

              case "reconstruct":
                dispX[idx] = (dispX[idx] ?? 0) * (1 - strength * 0.1);
                dispY[idx] = (dispY[idx] ?? 0) * (1 - strength * 0.1);
                break;

              case "twirl-clockwise":
              case "twirl-counter": {
                const dir = params.mode === "twirl-clockwise" ? 1 : -1;
                const angle = strength * dir * 0.1;
                const cosA = Math.cos(angle);
                const sinA = Math.sin(angle);
                const newDx = dx * cosA - dy * sinA - dx;
                const newDy = dx * sinA + dy * cosA - dy;
                dispX[idx] = (dispX[idx] ?? 0) + newDx;
                dispY[idx] = (dispY[idx] ?? 0) + newDy;
                break;
              }

              case "pinch":
                dispX[idx] = (dispX[idx] ?? 0) - dx * strength * 0.05;
                dispY[idx] = (dispY[idx] ?? 0) - dy * strength * 0.05;
                break;

              case "bloat":
                dispX[idx] = (dispX[idx] ?? 0) + dx * strength * 0.05;
                dispY[idx] = (dispY[idx] ?? 0) + dy * strength * 0.05;
                break;

              case "freeze":
              case "thaw":
                break;
            }
          }
        }
      }
    }

    // Apply displacement map using backward mapping (inverse warping)
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        const o = idx * 4;

        // Source position (where this pixel came from)
        let sx = x - dispX[idx]!;
        let sy = y - dispY[idx]!;

        // Clamp to image bounds
        if (sx < 0 || sx >= w - 1 || sy < 0 || sy >= h - 1) {
          dst[o] = src[o]!;
          dst[o + 1] = src[o + 1]!;
          dst[o + 2] = src[o + 2]!;
          dst[o + 3] = src[o + 3]!;
          continue;
        }

        // Bilinear interpolation
        const x0 = Math.floor(sx);
        const y0 = Math.floor(sy);
        const x1 = x0 + 1;
        const y1 = y0 + 1;
        const fx = sx - x0;
        const fy = sy - y0;

        const i00 = (y0 * w + x0) * 4;
        const i10 = (y0 * w + x1) * 4;
        const i01 = (y1 * w + x0) * 4;
        const i11 = (y1 * w + x1) * 4;

        for (let c = 0; c < 4; c++) {
          const v00 = src[i00 + c]!;
          const v10 = src[i10 + c]!;
          const v01 = src[i01 + c]!;
          const v11 = src[i11 + c]!;
          const v = v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
          dst[o + c] = clamp8(Math.round(v));
        }
      }
    }

    return out;
  },
};
